import { ITable } from 'src/utils/commonInterfaces';
import CallCenterDashboardSharedTables from './CallCenterDashboardSharedTables'
import { useEffect, useState } from 'react';
import helper from 'src/utils/helper';
import { useParams } from 'react-router-dom';
import { Image, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { errorMessage } from 'src/utils/commonStringsConsts';
import { TableWrapper } from 'src/utils/CallCenterTableWrapper';
import CallCenterTable from './callCenterTable';
import tableHeaderConverter from 'src/utils/tableHeaderConverter';
import { LostUsersTableConfig } from '../../../shared/src/utils/commonInterfaces';

const NidPhoto = ({ src }: { src?: string }) =>
    src ? (
        <Image
            width={80}
            height={80}
            src={src}
            className='object-cover'
            alt='NID Photo'
            preview={{
                mask: (
                    <>
                        <EyeOutlined /> Preview
                    </>
                ),
                getContainer: () => document.body,
            }}
        />
    ) : null;

const formatNidPhotoRows = (data: any[]) =>
    data?.length > 0
        ? data.map((item: any) => ({
            ...item,
            idLink1: <NidPhoto src={item?.idLink1} />,
            idLink2: <NidPhoto src={item?.idLink2} />,
        }))
        : [];

const lostUsersTables: LostUsersTableConfig[] = [
    {
        wrapperTitle: 'Bills Payment',
        tableTitle: 'Lost Users - Bills Payment',
        errorLabel: 'Bills Payment Data',
        fetch: (userId: number) => helper.getCallCenterBillsPayment(userId),
        formatRows: (data: any[]) => data?.map((item: any) => ({
            ...item,
            dateOfRequest: helper.formatDate(item['dateOfRequest'], 'YYYY/MM/DD HH:mm:ss'),
        })),
    },
    {
        wrapperTitle: 'Lost Users ID Data',
        tableTitle: 'Lost Users Data',
        errorLabel: 'Lost Users Data',
        fetch: (userId: number) => helper.getCallCenterLostUsersData(userId),
        formatRows: formatNidPhotoRows,
    },
    {
        wrapperTitle: 'Lost Users NID Photo Links',
        tableTitle: 'NID Photo Links',
        errorLabel: 'NID Photo Links',
        fetch: (userId: number) => helper.getCallCenterLostUsersNidPhotoLinks(userId),
        formatRows: formatNidPhotoRows,
    },
    {
        wrapperTitle: 'User Lost Deductions',
        tableTitle: 'User Lost Deductions',
        errorLabel: 'User Lost Deductions',
        fetch: (userId: number) => helper.getCallCenterUserLostDeductions(userId),
        formatRows: (data) => data?.map((item: any) => ({
            ...item,
            expectedCutOffDate: helper.formatDate(item['expectedCutOffDate'], 'MMMM D, YYYY, h:mm A'),
        })),
    },
];

export default function CallCenterLostUsersDashboard() {
    const { userId: userID } = useParams<{ userId: string }>();
    const [tables, setTables] = useState<ITable[]>(lostUsersTables.map(() => ({ data: [], columns: [], loading: true })));

    const setTable = (index: number, value: ITable) => {
        setTables((prev) => prev.map((table, tableIndex) => (tableIndex === index ? value : table)));
    };

    const fetchAllLostUsersData = async () => {
        const userId = Number(userID);

        await Promise.allSettled(
            lostUsersTables.map(async (config, index) => {
                try {
                    const res: any = await config.fetch(userId);
                    if (res?.status !== 200) {
                        throw new Error(config.errorLabel);
                    }

                    const data = res?.data?.data ?? [];
                    const formattedData = config.formatRows ? config.formatRows(data) : data;
                    setTable(index, {
                        data: formattedData,
                        columns: data?.length > 0 ? tableHeaderConverter(data) : [],
                        loading: false,
                    });
                } catch {
                    message.error(`${errorMessage} with ${config.errorLabel}`);
                    setTable(index, { data: [], columns: [], loading: false });
                }
            })
        );
    };

    useEffect(() => {
        fetchAllLostUsersData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="bg-white callCenterMainDashboardHeader">Call Center Lost Users Dashboard</div>
            <div className="callCenterMainDashboardContainer">
                <CallCenterDashboardSharedTables />
                <div className="unPaginatedTables">
                    {lostUsersTables.map((config, index) => {
                        const table = tables[index];
                        return (
                            <TableWrapper key={index} title={config.wrapperTitle}>
                                <CallCenterTable
                                    columns={table?.columns}
                                    rows={table?.data}
                                    pagination={false}
                                    loading={table?.loading}
                                    scrolling={table?.data?.length ? { x: 'max-content' } : { y: 240 }}
                                    title={config.tableTitle}
                                />
                            </TableWrapper>
                        );
                    })}
                </div>
            </div>
        </>
    )
}
